import { useCallback, useEffect, useRef, useState } from 'react'
import { io, type Socket } from 'socket.io-client'
import { getAccessToken } from '@services/auth/accessTokenStore'
import { isStopTranscript } from '@utils/charting-voice-transcript.utils'
import { pcmToBase64 } from '@utils/perio-audio.utils'
import {
  useVoiceTranscriptFeed,
  VoiceTranscriptEntry,
  VoiceTranscriptStatus,
} from '@hooks/useVoiceTranscriptFeed'

export type StreamingVoicePhase =
  | 'idle'
  | 'connecting'
  | 'listening'
  | 'processing'
  | 'error'

export interface FinalTranscriptEvent<TChart> {
  text: string
  normalized?: string
  failed: boolean
  chart: TChart
  turnChart: TChart
  syncChart: (next: TChart) => void
  rebaseQueued: (map: (turnChart: TChart) => TChart) => void
  discardQueued: () => void
  isCurrent: () => boolean
  /** Also records the outcome on this phrase's transcript entry. */
  setMessage: (message: string, status?: VoiceTranscriptStatus) => void
  setPaused: (paused: boolean) => void
}

type ChartIds = { patient_id: string; clinic_id: string }

type StopOptions = {
  preserveMessage?: boolean
  message?: string
}

type VoiceTurn<TChart> = {
  chart: TChart
  started: number
  committed: boolean
  id?: string
  discarded?: boolean
}

type VoiceTranscript = {
  partial: string
  final?: string
  formatted?: string
  normalized?: string
  failed?: boolean
}

const createSessionId = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return `voice_${Date.now()}_${Math.random().toString(36).slice(2)}`
}

export function useStreamingVoiceSession<TChart extends ChartIds>({
  enabled,
  contextKey,
  chart,
  resolveBaseUrl,
  socketPath,
  eventPrefix,
  formatTranscript,
  listeningMessage,
  onStartReset,
  onStopReset,
  onFinalTranscript,
}: {
  enabled: boolean
  contextKey: string
  chart: TChart
  resolveBaseUrl: () => string
  socketPath: string
  eventPrefix: string
  formatTranscript: (transcript: string) => string
  listeningMessage: () => string
  onStartReset: () => void
  onStopReset: () => void
  onFinalTranscript: (
    event: FinalTranscriptEvent<TChart>,
  ) => 'continue' | 'stop' | Promise<'continue' | 'stop'>
}): {
  phase: StreamingVoicePhase
  message: string
  setMessage: (message: string) => void
  toggle: () => void
  stop: (options?: StopOptions) => void
  backlog: number
  recognized: string
  liveText: string
  history: VoiceTranscriptEntry[]
  queued: string[]
  discardInFlight: () => void
  isRunning: () => boolean
  setPaused: (paused: boolean) => void
} {
  const [phase, setPhase] = useState<StreamingVoicePhase>('idle')
  const [message, setMessage] = useState('')
  const [backlog, setBacklog] = useState(0)
  const feed = useVoiceTranscriptFeed(formatTranscript)
  const feedRef = useRef(feed)
  feedRef.current = feed
  const latest = useRef({
    chart,
    enabled,
    contextKey,
    resolveBaseUrl,
    socketPath,
    eventPrefix,
    formatTranscript,
    listeningMessage,
    onStartReset,
    onStopReset,
    onFinalTranscript,
  })
  latest.current = {
    chart,
    enabled,
    contextKey,
    resolveBaseUrl,
    socketPath,
    eventPrefix,
    formatTranscript,
    listeningMessage,
    onStartReset,
    onStopReset,
    onFinalTranscript,
  }
  const generation = useRef(0)
  const resources = useRef<{
    stream?: MediaStream
    audio?: AudioContext
    node?: AudioWorkletNode
    socket?: Socket
    sessionId?: string
    eventPrefix?: string
    watchdog?: ReturnType<typeof setInterval>
  }>({})
  const running = useRef(false)
  const pausedRef = useRef(false)
  const discardInFlightRef = useRef<() => void>(() => {})

  const stop = useCallback((options?: StopOptions): void => {
    const wasRunning = running.current
    generation.current++
    running.current = false
    pausedRef.current = false
    discardInFlightRef.current = () => {}
    const old = resources.current
    resources.current = {}
    if (old.socket && old.sessionId && old.eventPrefix) {
      old.socket.emit(`${old.eventPrefix}:stop`, { sessionId: old.sessionId })
    }
    old.socket?.removeAllListeners()
    old.socket?.disconnect()
    clearInterval(old.watchdog)
    old.node?.disconnect()
    old.stream?.getTracks().forEach((track) => track.stop())
    void old.audio?.close()
    feedRef.current.discard()
    if (wasRunning) {
      latest.current.onStopReset()
      setBacklog(0)
      setPhase('idle')
      if (!options?.preserveMessage) {
        setMessage(
          options?.message ?? 'Stopped. Any unfinished phrase was not applied.',
        )
      }
    }
  }, [])

  useEffect(() => {
    return (): void => {
      stop()
    }
  }, [contextKey, enabled, stop])

  useEffect(() => {
    feedRef.current.reset()
    setMessage('')
  }, [contextKey])

  const discardInFlight = useCallback((): void => {
    discardInFlightRef.current()
  }, [])

  const isRunning = useCallback((): boolean => running.current, [])

  const setPaused = useCallback((paused: boolean): void => {
    if (paused === pausedRef.current) return
    pausedRef.current = paused
    if (paused) discardInFlightRef.current()
    const active = resources.current
    if (active.socket && active.sessionId && active.eventPrefix) {
      active.socket.emit(`${active.eventPrefix}:pause`, {
        sessionId: active.sessionId,
        paused,
      })
    }
  }, [])

  const start = async (): Promise<void> => {
    if (!latest.current.enabled || running.current) return
    running.current = true
    pausedRef.current = false
    latest.current.onStartReset()
    feedRef.current.reset()
    const gen = ++generation.current
    const key = latest.current.contextKey
    const prefix = latest.current.eventPrefix
    const sessionId = createSessionId()
    let proxyReady = false
    const valid = (): boolean =>
      gen === generation.current &&
      latest.current.enabled &&
      key === latest.current.contextKey
    const fail = (error: unknown): void => {
      if (!valid()) return
      const reason =
        error instanceof Error
          ? error.message
          : 'Unable to connect streaming transcription.'
      stop()
      setPhase('error')
      setMessage(reason)
    }
    setPhase('connecting')
    setMessage('Connecting streaming transcription…')
    const turns: VoiceTurn<TChart>[] = []
    const transcripts = new Map<string, VoiceTranscript>()
    const finished = new Set<string>()
    let previousItem: string | null = null
    let currentTurn: VoiceTurn<TChart> | undefined
    let draining = false
    let captureStarted = false
    // Assigned once the worklet exists. Mic input stays disconnected until ready.
    let armCapture: () => void = () => {}
    const started = Date.now()
    const refreshBacklog = (): void =>
      setBacklog(turns.filter((turn) => !turn.discarded).length)
    const previousTurnIdAt = (index: number): string | null =>
      index > 0 ? (turns[index - 1]?.id ?? null) : null
    const discardQueued = (): void => {
      for (const turn of turns) turn.discarded = true
      feedRef.current.discard()
      refreshBacklog()
      const active = resources.current
      if (active.socket && active.sessionId) {
        active.socket.emit(`${prefix}:clear`, { sessionId: active.sessionId })
      }
    }
    discardInFlightRef.current = discardQueued
    const rebaseQueued = (map: (turnChart: TChart) => TChart): void => {
      for (const queued of turns) queued.chart = map(queued.chart)
    }
    const syncChart = (next: TChart): void => {
      latest.current.chart = next
    }
    const drain = async (): Promise<void> => {
      if (draining) return
      draining = true
      try {
        while (valid() && turns[0]?.id) {
          const turn = turns[0]
          const id = turn.id!
          const transcript = transcripts.get(id)
          if (transcript?.final === undefined && !transcript?.failed) break
          turns.shift()
          transcripts.delete(id)
          finished.add(id)
          if (!turn.discarded) {
            const finalText = transcript?.formatted ?? transcript?.final ?? ''
            const turnFeed = feedRef.current
            turnFeed.finalize(
              id,
              transcript?.formatted ??
                latest.current.formatTranscript(finalText),
              {
                status: transcript?.failed ? 'failed' : 'pending',
                awaitResult: false,
                heard: transcript?.final,
              },
            )
            const pending = latest.current.onFinalTranscript({
              text: finalText,
              normalized: transcript?.normalized,
              failed: Boolean(transcript?.failed),
              chart: latest.current.chart,
              turnChart: turn.chart,
              syncChart,
              rebaseQueued,
              discardQueued,
              isCurrent: valid,
              setMessage: (next, status = 'info') => {
                setMessage(next)
                turnFeed.annotate({ id, note: next, status })
              },
              setPaused,
            })
            const action =
              typeof pending === 'object' &&
              pending !== null &&
              typeof (pending as Promise<'continue' | 'stop'>).then ===
                'function'
                ? await pending
                : (pending as 'continue' | 'stop')
            turnFeed.annotate({ id, status: 'info', pendingOnly: true })
            if (action === 'stop') {
              turns.length = 0
              stop({ preserveMessage: true })
              return
            }
          } else {
            feedRef.current.drop(id)
          }
        }
      } finally {
        draining = false
        refreshBacklog()
        const nextId = turns[0]?.id
        const nextTranscript = nextId ? transcripts.get(nextId) : undefined
        if (
          valid() &&
          nextId &&
          (nextTranscript?.final !== undefined || nextTranscript?.failed)
        )
          queueMicrotask(() => void drain())
      }
    }
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error(
          'Microphone capture is unavailable. Use a supported browser over HTTPS (or localhost for development).',
        )
      }
      const token = getAccessToken()
      const dbName = localStorage.getItem('db_name')
      if (!token) throw new Error('Sign in again before using voice.')
      const socket = io(latest.current.resolveBaseUrl(), {
        path: latest.current.socketPath,
        transports: ['websocket'],
        reconnection: false,
        autoConnect: false,
        auth: { token, dbName: dbName ?? undefined },
      })
      resources.current.socket = socket
      resources.current.sessionId = sessionId
      resources.current.eventPrefix = prefix
      socket.on('connect_error', (error: Error) => fail(error))
      socket.on('disconnect', (reason: string) => {
        if (running.current && valid()) {
          fail(new Error(`Voice connection closed: ${reason}`))
        }
      })
      socket.on(
        `${prefix}:partial`,
        (payload: { sessionId: string; itemId: string; text: string }) => {
          if (!valid() || payload.sessionId !== sessionId) return
          if (typeof payload.text !== 'string' || payload.text.length > 4000) {
            fail(new Error('Transcript too long. Repeat a shorter phrase.'))
            return
          }
          const entry = transcripts.get(payload.itemId) ?? { partial: '' }
          entry.partial = payload.text
          transcripts.set(payload.itemId, entry)
          if (!turns.find((turn) => turn.id === payload.itemId)?.discarded) {
            feedRef.current.partial(payload.itemId, payload.text)
          }
        },
      )
      socket.on(
        `${prefix}:committed`,
        (payload: {
          sessionId: string
          itemId: string
          previousItemId?: string | null
        }) => {
          if (!valid() || payload.sessionId !== sessionId) return
          try {
            if (finished.has(payload.itemId)) return
            if (turns.some((turn) => turn.id === payload.itemId)) return
            const replacementIndex = turns.findIndex(
              (turn, index) =>
                turn.id &&
                !finished.has(turn.id) &&
                previousTurnIdAt(index) === (payload.previousItemId ?? null),
            )
            if (replacementIndex >= 0) {
              const turn = turns[replacementIndex]
              const previousId = turn.id!
              transcripts.delete(previousId)
              finished.delete(previousId)
              feedRef.current.drop(previousId)
              turn.id = payload.itemId
              if (turn.discarded) feedRef.current.drop(payload.itemId)
              if (previousItem === previousId) previousItem = payload.itemId
              void drain()
              return
            }
            const turn = turns.find((entry) => entry.committed && !entry.id)
            if (!turn || (payload.previousItemId ?? null) !== previousItem) {
              throw new Error(
                'Speech arrived out of order. Restart and repeat unfinished speech.',
              )
            }
            turn.id = payload.itemId
            if (turn.discarded) feedRef.current.drop(payload.itemId)
            previousItem = payload.itemId
            void drain()
          } catch (error) {
            fail(error)
          }
        },
      )
      socket.on(
        `${prefix}:final`,
        (payload: {
          sessionId: string
          itemId: string
          text: string
          formatted?: string
          normalized?: string
          failed?: boolean
        }) => {
          if (!valid() || payload.sessionId !== sessionId) return
          try {
            if (finished.has(payload.itemId)) return
            if (
              !payload.failed &&
              typeof payload.text === 'string' &&
              isStopTranscript(payload.text)
            ) {
              feedRef.current.finalize(payload.itemId, payload.text, {
                status: 'applied',
              })
              stop({
                message:
                  'Microphone stopped. Unfinished and queued speech discarded. Click the microphone to start again.',
              })
              return
            }
            const entry = transcripts.get(payload.itemId) ?? { partial: '' }
            if (payload.failed) entry.failed = true
            else {
              if (
                typeof payload.text !== 'string' ||
                payload.text.length > 4000
              ) {
                throw new Error('Invalid final transcript.')
              }
              if (entry.final !== undefined && entry.final !== payload.text) {
                throw new Error(
                  'Conflicting final transcripts. Restart the microphone.',
                )
              }
              entry.final = payload.text
              if (typeof payload.formatted === 'string') {
                entry.formatted = payload.formatted
              }
              if (typeof payload.normalized === 'string') {
                if (payload.normalized.length > 4000) {
                  throw new Error('Invalid final transcript.')
                }
                entry.normalized = payload.normalized
              }
            }
            transcripts.set(payload.itemId, entry)
            const queuedText = entry.formatted ?? entry.final
            if (
              queuedText &&
              !turns.find((turn) => turn.id === payload.itemId)?.discarded
            ) {
              feedRef.current.partial(payload.itemId, queuedText)
            }
            if (transcripts.size > 16) {
              throw new Error(
                'Too many unfinished transcripts. Restart the microphone.',
              )
            }
            void drain()
          } catch (error) {
            fail(error)
          }
        },
      )
      socket.on(
        `${prefix}:error`,
        (payload: { sessionId?: string; message: string }) => {
          if (!valid()) return
          if (payload.sessionId && payload.sessionId !== sessionId) return
          fail(new Error(payload.message))
        },
      )
      socket.on(`${prefix}:stopped`, (payload: { sessionId: string }) => {
        if (!valid() || payload.sessionId !== sessionId) return
        stop({
          message: 'Microphone stopped. Click the microphone to start again.',
        })
      })
      socket.on(
        `${prefix}:ready`,
        (payload: { sessionId: string; message?: string }) => {
          if (!valid() || payload.sessionId !== sessionId) return
          proxyReady = true
          setPhase('listening')
          setMessage(payload.message || latest.current.listeningMessage())
          armCapture()
        },
      )
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      })
      if (!valid()) {
        stream.getTracks().forEach((track) => track.stop())
        return
      }
      resources.current.stream = stream
      const audio = new AudioContext({ sampleRate: 24000 })
      resources.current.audio = audio
      await audio.audioWorklet.addModule('/perio-stream-capture.js')
      if (!valid()) return
      if (audio.sampleRate !== 24000) {
        throw new Error(
          'This browser cannot capture at the required sample rate.',
        )
      }
      const node = new AudioWorkletNode(audio, 'perio-stream-capture')
      resources.current.node = node
      node.onprocessorerror = (): void =>
        fail(
          new Error(
            'Audio capture failed. Restart and repeat unfinished speech.',
          ),
        )
      const mute = audio.createGain()
      mute.gain.value = 0
      node.port.onmessage = (
        event: MessageEvent<{
          type: string
          audio?: Float32Array
          error?: string
        }>,
      ): void => {
        if (!valid() || !proxyReady) return
        try {
          if (event.data.type === 'error') throw new Error(event.data.error)
          // Keyword pause still streams audio so "resume" can be transcribed.
          if (event.data.type === 'start') {
            if (turns.length >= 8 || currentTurn) {
              throw new Error(
                'Too many unfinished phrases. Restart and repeat unfinished speech.',
              )
            }
            currentTurn = {
              chart: structuredClone(latest.current.chart),
              started: Date.now(),
              committed: false,
            }
            turns.push(currentTurn)
            refreshBacklog()
          } else if (event.data.type === 'audio' && event.data.audio) {
            if (!currentTurn) throw new Error('Audio arrived without a phrase.')
            socket.emit(`${prefix}:audio.append`, {
              sessionId,
              audio: pcmToBase64(event.data.audio),
            })
          } else if (event.data.type === 'commit') {
            if (!currentTurn) throw new Error('Missing audio turn.')
            currentTurn.committed = true
            currentTurn = undefined
            socket.emit(`${prefix}:audio.commit`, { sessionId })
          }
        } catch (error) {
          fail(error)
        }
      }
      armCapture = (): void => {
        if (captureStarted || !valid()) return
        captureStarted = true
        audio.createMediaStreamSource(stream).connect(node)
        node.connect(mute).connect(audio.destination)
        void audio.resume().catch((error: unknown) => fail(error))
      }
      socket.on('connect', () => {
        if (!valid()) return
        socket.emit(`${prefix}:start`, {
          sessionId,
          patient_id: latest.current.chart.patient_id,
          clinic_id: latest.current.chart.clinic_id,
        })
      })
      socket.connect()
      resources.current.watchdog = setInterval(() => {
        if (!valid()) return
        if (Date.now() - started > 30 * 60 * 1000) {
          fail(
            new Error(
              'Voice session ended after 30 minutes. Restart the microphone to continue.',
            ),
          )
        } else if (!proxyReady && Date.now() - started > 45000) {
          fail(
            new Error(
              'Streaming connection timed out. Restart the microphone.',
            ),
          )
        } else if (turns[0] && Date.now() - turns[0].started > 45000) {
          fail(
            new Error(
              'Transcription is taking too long. Restart and repeat unfinished speech.',
            ),
          )
        }
      }, 1000)
      stream.getTracks().forEach((track) => {
        track.onended = (): void =>
          fail(new Error('Microphone disconnected. Restart to continue.'))
      })
    } catch (error) {
      fail(error)
    }
  }

  const toggle = (): void => {
    if (running.current) stop()
    else void start()
  }

  return {
    phase,
    message,
    setMessage,
    toggle,
    stop,
    backlog,
    recognized: feed.recognized,
    liveText: feed.liveText,
    history: feed.history,
    queued: feed.queued,
    discardInFlight,
    isRunning,
    setPaused,
  }
}
